import { NS, type AuditEventV1, type NamespaceTuple } from '@ownyou/shared-types';
import type { WalletSigner } from '@ownyou/identity';
import {
  type AuditKeyStore,
  type AuditEventDraftV1,
  type StoredAuditKey,
  getOrCreateDelegatedAuditKey,
  signAuditEvent,
} from './audit-signing';
import type { AuditProducerEventDraft } from './producer';

export interface AuditStoreWriter {
  put<T>(namespace: NamespaceTuple, key: string, value: T): Promise<void>;
}

export interface AuditEventSinkConfig {
  store: AuditStoreWriter;
  signer: WalletSigner;
  keyStore: AuditKeyStore;
  deviceId: string;
}

export class AuditEventSink {
  private delegatedKeyPromise: Promise<StoredAuditKey> | null = null;

  constructor(private readonly config: AuditEventSinkConfig) {}

  async record(draft: AuditProducerEventDraft): Promise<AuditEventV1> {
    const walletAddress = this.config.signer.getAddress();
    const key = await this.getDelegatedKey();
    const eventDraft: AuditEventDraftV1 = {
      ...draft,
      userId: walletAddress,
      actor: draft.actor.kind === 'user' && !draft.actor.id
        ? { ...draft.actor, id: walletAddress }
        : draft.actor,
    };
    const event = await signAuditEvent(key, eventDraft);
    await this.config.store.put(NS.auditEvents(walletAddress), event.id, event);
    return event;
  }

  private getDelegatedKey(): Promise<StoredAuditKey> {
    this.delegatedKeyPromise ??= getOrCreateDelegatedAuditKey({
      signer: this.config.signer,
      deviceId: this.config.deviceId,
      keyStore: this.config.keyStore,
    }).catch((error) => {
      this.delegatedKeyPromise = null;
      throw error;
    });
    return this.delegatedKeyPromise;
  }
}
